'use client'

import {useCallback, useEffect, useMemo, useState} from 'react'
import {
    BarChart3,
    Clock,
    Loader2,
    RefreshCw,
    ShieldCheck,
    Smartphone,
    UserCheck,
    UserRound,
    Users,
} from 'lucide-react'
import {apiGet} from '@/lib/api'
import {PageHeader} from '@/components/PageHeader'

type OverviewStats = {
    totalUsers: number
    enabledUsers: number
    trialUsers: number
    expiringSoonUsers: number
    wechatInstances: number
}

type TrendPoint = {
    date: string
    newUsers: number
    trialUsers: number
}

type ActiveUser = {
    id: number
    username: string
    role: string
    enabled: number
    isTrial: number
    instanceCount: number
    lastSeenAt?: number | null
    lastLoginAt?: number | null
}

type LoginLog = {
    id: number
    userId: number
    username: string
    ip: string
    ipLocation?: string | null
    userAgent: string
    createdAt: number
}

type LocationStat = {
    name: string
    count: number
}

type UserInstanceStat = {
    userId: number
    username: string
    count: number
}

type AdminStats = {
    overview: OverviewStats
    registrationTrend: TrendPoint[]
    recentActiveUsers: ActiveUser[]
    recentLoginLogs: LoginLog[]
    loginLocationStats: LocationStat[]
    userInstanceRanking: UserInstanceStat[]
    generatedAt: number
}

const EMPTY_STATS: AdminStats = {
    overview: {
        totalUsers: 0,
        enabledUsers: 0,
        trialUsers: 0,
        expiringSoonUsers: 0,
        wechatInstances: 0,
    },
    registrationTrend: [],
    recentActiveUsers: [],
    recentLoginLogs: [],
    loginLocationStats: [],
    userInstanceRanking: [],
    generatedAt: 0,
}

type DaysRange = 7 | 30

export function AdminStatsView() {
    const [stats, setStats] = useState<AdminStats>(EMPTY_STATS)
    const [days, setDays] = useState<DaysRange>(30)
    const [loading, setLoading] = useState(true)

    const fetchStats = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true)
        try {
            const res = await apiGet<{success: boolean; stats: AdminStats}>(`/stats/overview?days=${days}`, {silent: true})
            if (res.success && res.stats) {
                setStats({
                    ...EMPTY_STATS,
                    ...res.stats,
                    overview: {...EMPTY_STATS.overview, ...res.stats.overview},
                    registrationTrend: res.stats.registrationTrend || [],
                    recentActiveUsers: res.stats.recentActiveUsers || [],
                    recentLoginLogs: res.stats.recentLoginLogs || [],
                    loginLocationStats: res.stats.loginLocationStats || [],
                    userInstanceRanking: res.stats.userInstanceRanking || [],
                })
            }
        } catch {
            // Toasting is handled by apiGet when not silent; this view keeps the panel quiet.
        } finally {
            if (showLoading) setLoading(false)
        }
    }, [days])

    useEffect(() => {
        fetchStats()
    }, [fetchStats])

    const overviewCards = [
        {label: '用户总数', value: stats.overview.totalUsers, helper: '系统账户', Icon: Users, tone: 'text-blue-600', bg: 'bg-blue-50'},
        {label: '启用用户', value: stats.overview.enabledUsers, helper: enabledRatio(stats.overview), Icon: UserCheck, tone: 'text-emerald-600', bg: 'bg-emerald-50'},
        {label: '试用用户', value: stats.overview.trialUsers, helper: '试用账号', Icon: ShieldCheck, tone: 'text-amber-600', bg: 'bg-amber-50'},
        {label: '即将过期', value: stats.overview.expiringSoonUsers, helper: '未来 7 天', Icon: Clock, tone: 'text-rose-600', bg: 'bg-rose-50'},
        {label: '微信实例', value: stats.overview.wechatInstances, helper: '已绑定实例', Icon: Smartphone, tone: 'text-cyan-600', bg: 'bg-cyan-50'},
    ]

    return (
        <div className="h-full flex flex-col bg-page">
            <PageHeader
                title="统计概览"
                description="用户、实例与登录行为总览"
                icon={<BarChart3 size={18}/>}
                action={(
                    <>
                        <div className="inline-flex h-8 rounded-md border border-hair bg-white p-0.5">
                            {[7, 30].map((value) => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`px-3 rounded text-xs transition-colors ${
                                        days === value ? 'bg-accent text-white' : 'text-soft hover:bg-page'
                                    }`}
                                    onClick={() => setDays(value as DaysRange)}
                                >
                                    近{value}天
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => fetchStats()}
                            disabled={loading}
                        >
                            {loading ? <Loader2 size={15} className="animate-spin"/> : <RefreshCw size={15}/>}
                            刷新
                        </button>
                    </>
                )}
            />

            <main className="flex-1 overflow-y-auto p-6">
                <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                    {overviewCards.map(({label, value, helper, Icon, tone, bg}) => (
                        <div key={label} className="card card-pad">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="text-xs text-muted">{label}</div>
                                    <div className="mt-2 text-2xl font-semibold text-ink">{formatNumber(value)}</div>
                                </div>
                                <span className={`h-9 w-9 rounded-md ${bg} ${tone} flex items-center justify-center`}>
                                    <Icon size={18}/>
                                </span>
                            </div>
                            <div className="mt-3 text-xs text-muted">{helper}</div>
                        </div>
                    ))}
                </section>

                <section className="mt-4 grid grid-cols-1 xl:grid-cols-[1.45fr_1fr] gap-4">
                    <div className="card card-pad min-h-[320px]">
                        <PanelTitle title="用户注册趋势" desc={`按天统计近 ${days} 天新增用户与试用用户`}/>
                        <RegistrationChart points={stats.registrationTrend}/>
                    </div>

                    <div className="card card-pad min-h-[320px]">
                        <PanelTitle title="登录 IP / 地区分布" desc={`近 ${days} 天登录来源`}/>
                        <div className="mt-5 space-y-4">
                            {stats.loginLocationStats.length ? stats.loginLocationStats.map((item) => (
                                <BarRow key={item.name} label={item.name} value={item.count} max={maxLocationCount(stats.loginLocationStats)}/>
                            )) : (
                                <EmptyBlock text="暂无登录地区数据"/>
                            )}
                        </div>
                        <div className="mt-6 grid grid-cols-3 gap-3">
                            <MiniMetric label="内网" value={locationCount(stats.loginLocationStats, '内网')}/>
                            <MiniMetric label="已识别地区" value={recognizedLocations(stats.loginLocationStats)}/>
                            <MiniMetric label="未知" value={locationCount(stats.loginLocationStats, '未知')}/>
                        </div>
                    </div>
                </section>

                <section className="mt-4 grid grid-cols-1 xl:grid-cols-[1.15fr_1.35fr] gap-4">
                    <div className="card overflow-hidden">
                        <div className="px-5 pt-5 pb-3">
                            <PanelTitle title="最近活跃用户" desc="按最后活跃时间排序"/>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-page text-xs text-muted">
                                <tr>
                                    <th className="text-left font-medium px-5 py-3">用户</th>
                                    <th className="text-left font-medium px-3 py-3">角色</th>
                                    <th className="text-left font-medium px-3 py-3">实例</th>
                                    <th className="text-left font-medium px-3 py-3">最后活跃</th>
                                    <th className="text-left font-medium px-5 py-3">状态</th>
                                </tr>
                                </thead>
                                <tbody>
                                {stats.recentActiveUsers.length ? stats.recentActiveUsers.map((user) => (
                                    <tr key={user.id} className="border-t border-hair/70">
                                        <td className="px-5 py-3">
                                            <div className="flex items-center gap-2">
                                                <span className="h-7 w-7 rounded-md bg-emerald-50 text-accent flex items-center justify-center">
                                                    <UserRound size={15}/>
                                                </span>
                                                <span className="font-medium text-ink">{user.username || '-'}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 text-soft">{user.role === 'admin' ? '管理员' : '用户'}</td>
                                        <td className="px-3 py-3 text-soft">{user.instanceCount}</td>
                                        <td className="px-3 py-3 text-soft whitespace-nowrap">{relativeTime(user.lastSeenAt || user.lastLoginAt)}</td>
                                        <td className="px-5 py-3">
                                            <StatusPill enabled={user.enabled === 1} trial={user.isTrial === 1}/>
                                        </td>
                                    </tr>
                                )) : (
                                    <TableEmpty colSpan={5}/>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="card overflow-hidden">
                        <div className="px-5 pt-5 pb-3">
                            <PanelTitle title="最近登录记录" desc="最新系统账号登录行为"/>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-page text-xs text-muted">
                                <tr>
                                    <th className="text-left font-medium px-5 py-3">用户</th>
                                    <th className="text-left font-medium px-3 py-3">IP</th>
                                    <th className="text-left font-medium px-3 py-3">地区</th>
                                    <th className="text-left font-medium px-3 py-3">设备</th>
                                    <th className="text-left font-medium px-5 py-3">登录时间</th>
                                </tr>
                                </thead>
                                <tbody>
                                {stats.recentLoginLogs.length ? stats.recentLoginLogs.map((log) => (
                                    <tr key={log.id} className="border-t border-hair/70">
                                        <td className="px-5 py-3 font-medium text-ink">{log.username || `用户 ${log.userId}`}</td>
                                        <td className="px-3 py-3 text-soft whitespace-nowrap">{log.ip || '-'}</td>
                                        <td className="px-3 py-3 text-soft">{log.ipLocation || '未知'}</td>
                                        <td className="px-3 py-3 text-soft max-w-[180px] truncate" title={log.userAgent}>{deviceName(log.userAgent)}</td>
                                        <td className="px-5 py-3 text-soft whitespace-nowrap">{formatDateTime(log.createdAt)}</td>
                                    </tr>
                                )) : (
                                    <TableEmpty colSpan={5}/>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                <section className="mt-4 card card-pad">
                    <PanelTitle title="每个用户绑定的微信实例数量" desc="按绑定实例数排序"/>
                    <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                        {stats.userInstanceRanking.length ? stats.userInstanceRanking.map((item) => (
                            <BarRow
                                key={item.userId}
                                label={item.username}
                                value={item.count}
                                max={maxInstanceCount(stats.userInstanceRanking)}
                                accent="bg-cyan-500"
                            />
                        )) : (
                            <EmptyBlock text="暂无绑定实例数据"/>
                        )}
                    </div>
                </section>
            </main>
        </div>
    )
}

function PanelTitle({title, desc}: {title: string; desc?: string}) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div>
                <h3 className="text-sm font-medium text-ink">{title}</h3>
                {desc ? <p className="mt-1 text-xs text-muted">{desc}</p> : null}
            </div>
        </div>
    )
}

function RegistrationChart({points}: {points: TrendPoint[]}) {
    const chart = useMemo(() => buildChart(points), [points])
    if (!points.length) {
        return <EmptyBlock text="暂无注册趋势数据" className="mt-10 h-[220px]"/>
    }
    return (
        <div className="mt-5">
            <div className="h-[230px] rounded-md border border-hair bg-white">
                <svg viewBox="0 0 720 230" className="h-full w-full" role="img" aria-label="用户注册趋势">
                    {[0, 1, 2, 3].map((line) => (
                        <line
                            key={line}
                            x1="42"
                            x2="690"
                            y1={28 + line * 52}
                            y2={28 + line * 52}
                            stroke="#edf1f5"
                            strokeWidth="1"
                        />
                    ))}
                    <polyline points={chart.newUserPoints} fill="none" stroke="#19a765" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points={chart.trialUserPoints} fill="none" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    {chart.markers.map((marker) => (
                        <g key={`${marker.date}-${marker.x}`}>
                            <circle cx={marker.x} cy={marker.y} r="3.5" fill="#19a765"/>
                        </g>
                    ))}
                </svg>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 text-xs text-muted">
                <div className="flex items-center gap-4">
                    <span className="inline-flex items-center gap-1.5"><i className="h-2 w-2 rounded-full bg-accent"/>新增用户</span>
                    <span className="inline-flex items-center gap-1.5"><i className="h-2 w-2 rounded-full bg-amber-500"/>试用用户</span>
                </div>
                <span>峰值 {chart.maxValue}</span>
            </div>
            <div className="mt-2 flex justify-between text-[11px] text-muted">
                <span>{points[0]?.date}</span>
                <span>{points[Math.floor(points.length / 2)]?.date}</span>
                <span>{points[points.length - 1]?.date}</span>
            </div>
        </div>
    )
}

function buildChart(points: TrendPoint[]) {
    const width = 648
    const height = 174
    const left = 42
    const top = 26
    const maxValue = Math.max(1, ...points.flatMap((point) => [point.newUsers, point.trialUsers]))
    const xFor = (index: number) => left + (points.length <= 1 ? width / 2 : (width * index) / (points.length - 1))
    const yFor = (value: number) => top + height - (value / maxValue) * height
    const toPoints = (key: 'newUsers' | 'trialUsers') =>
        points.map((point, index) => `${xFor(index).toFixed(1)},${yFor(point[key]).toFixed(1)}`).join(' ')
    return {
        newUserPoints: toPoints('newUsers'),
        trialUserPoints: toPoints('trialUsers'),
        maxValue,
        markers: points.map((point, index) => ({date: point.date, x: xFor(index), y: yFor(point.newUsers)})),
    }
}

function BarRow({label, value, max, accent = 'bg-accent'}: {label: string; value: number; max: number; accent?: string}) {
    const width = max > 0 ? Math.max(6, Math.round((value / max) * 100)) : 0
    return (
        <div className="grid grid-cols-[90px_1fr_42px] items-center gap-3 text-sm">
            <div className="text-soft truncate" title={label}>{label}</div>
            <div className="h-2.5 rounded-full bg-page overflow-hidden">
                <div className={`h-full rounded-full ${accent}`} style={{width: `${width}%`}}/>
            </div>
            <div className="text-right font-medium text-ink">{formatNumber(value)}</div>
        </div>
    )
}

function MiniMetric({label, value}: {label: string; value: number}) {
    return (
        <div className="rounded-md border border-hair bg-page px-3 py-2">
            <div className="text-[11px] text-muted">{label}</div>
            <div className="mt-1 text-base font-semibold text-ink">{formatNumber(value)}</div>
        </div>
    )
}

function StatusPill({enabled, trial}: {enabled: boolean; trial: boolean}) {
    if (!enabled) return <span className="pill">禁用</span>
    if (trial) return <span className="inline-flex items-center h-6 px-2 rounded bg-amber-50 text-amber-700 text-xs">试用</span>
    return <span className="pill-green">启用</span>
}

function EmptyBlock({text, className = 'h-24'}: {text: string; className?: string}) {
    return (
        <div className={`${className} rounded-md border border-dashed border-hair bg-page/60 flex items-center justify-center text-sm text-muted`}>
            {text}
        </div>
    )
}

function TableEmpty({colSpan}: {colSpan: number}) {
    return (
        <tr>
            <td colSpan={colSpan} className="px-5 py-10 text-center text-sm text-muted">暂无数据</td>
        </tr>
    )
}

function enabledRatio(overview: OverviewStats) {
    if (!overview.totalUsers) return '启用率 0%'
    return `启用率 ${Math.round((overview.enabledUsers / overview.totalUsers) * 100)}%`
}

function maxLocationCount(items: LocationStat[]) {
    return Math.max(1, ...items.map((item) => item.count))
}

function maxInstanceCount(items: UserInstanceStat[]) {
    return Math.max(1, ...items.map((item) => item.count))
}

function locationCount(items: LocationStat[], name: string) {
    return items.find((item) => item.name === name)?.count || 0
}

function recognizedLocations(items: LocationStat[]) {
    return items
        .filter((item) => item.name !== '未知' && item.name !== '内网')
        .reduce((sum, item) => sum + item.count, 0)
}

function formatNumber(value: number) {
    return new Intl.NumberFormat('zh-CN').format(value || 0)
}

function formatDateTime(value?: number | null) {
    if (!value) return '-'
    const date = new Date(value)
    return `${date.getMonth() + 1}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function relativeTime(value?: number | null) {
    if (!value) return '从未'
    const diff = Date.now() - value
    if (diff < 60_000) return '刚刚'
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`
    if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)} 天前`
    return formatDateTime(value)
}

function deviceName(userAgent: string) {
    const text = userAgent || ''
    if (/Windows/i.test(text)) return 'Windows'
    if (/Macintosh|Mac OS/i.test(text)) return 'macOS'
    if (/iPhone/i.test(text)) return 'iPhone'
    if (/iPad/i.test(text)) return 'iPad'
    if (/Android/i.test(text)) return 'Android'
    if (/Linux/i.test(text)) return 'Linux'
    return text ? '浏览器' : '-'
}

function pad(value: number) {
    return String(value).padStart(2, '0')
}
